import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { IAreaPolygonEditHandle, IAreaPolygonItem } from "@ryde/layout";
import { CrudApi, IAreaDto, Log } from "@ryde/shared";

interface IUseAreaPolygonEditorArgs {
  areas: IAreaPolygonItem[];
  editorRef: MutableRefObject<IAreaPolygonEditHandle | undefined>;
  // Called after a successful save so the page can re-fetch and re-render.
  onSaved: () => void;
}

interface IUseAreaPolygonEditorResult {
  editingId: number | null;
  editingArea?: IAreaPolygonItem;
  isSaving: boolean;
  beginEdit: (id: number) => void;
  cancelEdit: () => void;
  saveEdit: () => Promise<void>;
}

// Owns the in-map "reshape a polygon" flow: which area is being edited, saving
// state, and persisting the reshaped polygon back onto the area's metadata.
// The edited area is captured as a frozen snapshot at edit start so re-fetches
// or selection changes can't remount the edit layer and discard in-progress
// edits — the page keeps the area selected for the duration instead.
export const useAreaPolygonEditor = ({
  areas,
  editorRef,
  onSaved,
}: IUseAreaPolygonEditorArgs): IUseAreaPolygonEditorResult => {
  const { t } = useTranslation();
  const [editingArea, setEditingArea] = useState<IAreaPolygonItem | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Always snapshot from the freshest areas without re-creating beginEdit.
  const areasRef = useRef(areas);
  areasRef.current = areas;

  const editingId = editingArea?.id ?? null;

  const beginEdit = useCallback((id: number): void => {
    const area = areasRef.current.find(item => item.id === id);

    if (area?.polygon?.features?.length) {
      setEditingArea({ ...area });
    }
  }, []);

  const cancelEdit = useCallback((): void => {
    setEditingArea(null);
  }, []);

  const saveEdit = useCallback(async (): Promise<void> => {
    if (editingArea === null || !editorRef.current) {
      return;
    }

    const { id } = editingArea;
    const polygon = editorRef.current.getEditedGeoJson();
    setIsSaving(true);

    try {
      // Read the area first so we preserve the rest of its metadata and only
      // swap the polygon — the PATCH replaces the whole metadata object.
      const area = await CrudApi.getById<IAreaDto>("area", id);
      const metadata = { ...(area?.metadata ?? {}), polygon };
      const updated = await CrudApi.update<IAreaDto>("area", id, { metadata });

      if (updated?.id) {
        Log.successAndToast(t("area.mapEditShapeSaved"));
        setEditingArea(null);
        onSaved();

        return;
      }

      Log.errorAndToast(t("area.mapEditShapeError"));
    } catch (error) {
      Log.errorAndToast(t("area.mapEditShapeError"), error);
    } finally {
      setIsSaving(false);
    }
  }, [editingArea, editorRef, onSaved, t]);

  // Escape cancels an in-progress edit (but not mid-save).
  useEffect(() => {
    if (editingArea === null) {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !isSaving) {
        setEditingArea(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [editingArea, isSaving]);

  return {
    editingId,
    editingArea: editingArea ?? undefined,
    isSaving,
    beginEdit,
    cancelEdit,
    saveEdit,
  };
};
